/**
 * FlywheelConfigModal Component
 * Enable or disable the autonomous follow-up Flywheel for an organization.
 *
 * Flywheel is a pure settings flag now — this modal just toggles it on/off.
 */

import React, { useState } from 'react';
import { Modal, Button, useToast, Typography, Alert, AlertDescription } from '@crisellevaldez/speculo-ui';
import { Organization } from '../../../types/organization.types';
import { useSaveFlywheelConfig } from '../../../hooks/organizations/useFlywheelConfig';

// ============================================================================
// Types
// ============================================================================

interface FlywheelConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOrg?: Organization;
  onSuccess?: () => void;
}

// ============================================================================
// Component
// ============================================================================

export const FlywheelConfigModal: React.FC<FlywheelConfigModalProps> = ({
  isOpen,
  onClose,
  selectedOrg,
  onSuccess,
}) => {
  const { showToast } = useToast();
  const { mutateAsync: saveConfig, isPending: isSaving } = useSaveFlywheelConfig();

  // ClientAdmin keys this modal per org, so the initial toggle state is correct
  // on every open.
  const isCurrentlyEnabled = selectedOrg?.settings?.flywheel_enabled === true;

  const [flywheelEnabled, setFlywheelEnabled] = useState(isCurrentlyEnabled);

  const handleSave = async () => {
    if (!selectedOrg) return;

    try {
      await saveConfig({
        organizationId: selectedOrg.id,
        flywheelEnabled,
      });

      showToast({
        message: flywheelEnabled
          ? 'Flywheel enabled successfully'
          : 'Flywheel disabled successfully',
        variant: 'success',
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error saving flywheel config:', error);
      showToast({
        message: error instanceof Error ? error.message : 'Failed to save flywheel configuration',
        variant: 'error',
      });
    }
  };

  if (!selectedOrg) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <Modal.Header>
        <Typography variant="h3">Flywheel Configuration</Typography>
      </Modal.Header>
      <Modal.Body className="py-8">
        <div className="space-y-6">
          <Alert variant="info">
            <AlertDescription>
              The Follow-up Flywheel is Remi&apos;s automated follow-up system. Remi analyzes each
              contact&apos;s activity (calls, website visits, emails) and schedules follow-ups at optimal
              times. This continues automatically until you hand off or remove contacts from the
              flywheel.
            </AlertDescription>
          </Alert>

          <div className="bg-gray-50 dark:bg-white/5 p-6 rounded-lg border border-gray-300 dark:border-white/10">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-bold text-foreground">Enable Follow-Up Flywheel</h4>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {flywheelEnabled
                    ? 'Flywheel is active for this organization'
                    : 'Flywheel is disabled for this organization'}
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={flywheelEnabled}
                  onChange={(e) => setFlywheelEnabled(e.target.checked)}
                  disabled={isSaving}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 dark:bg-white/10 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 dark:peer-focus:ring-amber-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 dark:after:border-gray-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
              </label>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex gap-2 w-full md:w-auto ml-auto justify-end">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} isLoading={isSaving}>
            Save Configuration
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default FlywheelConfigModal;
